import { Ticket } from './commandCenterTypes';

export type TicketScope = 'mine' | 'all';

export const normalizeTicketStatus = (status?: string | null): string => {
  const normalized = String(status || '').trim().toLowerCase();

  if (normalized === 'closed') return 'completed';
  if (normalized === 'in progress') return 'in-progress';
  if (normalized === 'on hold' || normalized === 'waiting') return 'on-hold';

  return normalized || 'open';
};

export const isTicketClosed = (status?: string | null): boolean =>
  normalizeTicketStatus(status) === 'completed';

const getCurrentEntity = (commandCentreAuth: any) => ({
  type: commandCentreAuth?.entity?.type,
  id: commandCentreAuth?.entity?.id,
});

export const isTicketAssignedToCurrentEntity = (
  ticket: any,
  commandCentreAuth: any
): boolean => {
  const { type: currentAssigneeType, id: currentAssigneeId } = getCurrentEntity(commandCentreAuth);

  if (!currentAssigneeId || !currentAssigneeType) return true;

  const assignees = Array.isArray(ticket.assignees) ? ticket.assignees : [];
  return assignees.some(
    (assignee: any) =>
      assignee &&
      assignee.type === currentAssigneeType &&
      Number(assignee.id) === Number(currentAssigneeId)
  );
};


export const isTicketCreatedByCurrentUser = (
  ticket: any,
  commandCentreAuth: any
): boolean => {
  const { type: currentAssigneeType, id: currentAssigneeId } = getCurrentEntity(commandCentreAuth);

  if (currentAssigneeType !== 'user' || currentAssigneeId == null) return false;

  const createdByUserId = ticket.createdByUserId ?? ticket.created_by_user_id;
  return createdByUserId != null && Number(createdByUserId) === Number(currentAssigneeId);
};

export const isTicketMine = (ticket: any, commandCentreAuth: any): boolean => {
  const { type: currentAssigneeType, id: currentAssigneeId } = getCurrentEntity(commandCentreAuth);

  if (!currentAssigneeId || !currentAssigneeType) return true;

  return (
    isTicketAssignedToCurrentEntity(ticket, commandCentreAuth) ||
    isTicketCreatedByCurrentUser(ticket, commandCentreAuth)
  );
};

export const applyTicketScope = <T extends { assignees?: unknown }>(
  tickets: T[],
  ticketScope: TicketScope,
  commandCentreAuth: any
): T[] => {
  if (ticketScope !== 'mine') return [...tickets];
  return tickets.filter((ticket) => isTicketMine(ticket, commandCentreAuth));
};

export const applyTicketKpiFilter = <T extends { task_urgency?: string; task_status?: string }>(
  tickets: T[],
  filterKey: string,
  ticketScope: TicketScope
): T[] => {
  switch (filterKey) {
    case 'minor':
      return tickets.filter(
        (ticket) => ticket.task_urgency === 'minor' && !isTicketClosed(ticket.task_status)
      );

    case 'major':
      return tickets.filter(
        (ticket) => ticket.task_urgency === 'major' && !isTicketClosed(ticket.task_status)
      );

    case 'emergency':
      return tickets.filter(
        (ticket) => ticket.task_urgency === 'emergency' && !isTicketClosed(ticket.task_status)
      );

    case 'open':
      return tickets.filter((ticket) => !isTicketClosed(ticket.task_status));

    case 'closed':
      return tickets.filter((ticket) => isTicketClosed(ticket.task_status));

    case 'all':
    default:
      return ticketScope === 'mine'
        ? tickets.filter((ticket) => !isTicketClosed(ticket.task_status))
        : [...tickets];
  }
};

export const getTicketStats = (tickets: Ticket[]) => ({
  total: tickets.length,
  minor: tickets.filter(
    (ticket) => ticket.task_urgency === 'minor' && !isTicketClosed(ticket.task_status)
  ).length,
  major: tickets.filter(
    (ticket) => ticket.task_urgency === 'major' && !isTicketClosed(ticket.task_status)
  ).length,
  emergency: tickets.filter(
    (ticket) => ticket.task_urgency === 'emergency' && !isTicketClosed(ticket.task_status)
  ).length,
  open: tickets.filter((ticket) => !isTicketClosed(ticket.task_status)).length,
  closed: tickets.filter((ticket) => isTicketClosed(ticket.task_status)).length,
});
